@file:OptIn(ExperimentalUuidApi::class)

package ai.wagemix.data

import ai.wagemix.net.client.FileUploadRequestBody
import ai.wagemix.net.client.WagemixClientAPI
import ai.wagemix.net.models.WMJDUrlUpload
import ai.wagemix.net.models.WMReevaluateJD
import ai.wagemix.net.models.WMUploadFile
import ai.wagemix.net.models.WMUploadFailureReason
import ai.wagemix.net.models.WMUploadProgress
import ai.wagemix.net.models.kxs
import ai.wagemix.net.models.of
import com.fsryan.foundation.data.FSSyncDataSource
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.onEach
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * The job-description pipeline of the Wagemix API (sync data source playbook). An upload or
 * re-evaluation starts the pipeline, then follows its progress stream into the user's local
 * context under the event-stream id until the final update; the terminal event says only that
 * the pipeline ended, the progress entry says what it produced. The API client supplies the
 * user's bearer token itself.
 */
interface WMJobDescriptionSyncDataSource: FSSyncDataSource<WMJobDescriptionSyncEvent<*>> {
    fun uploadJobDescription(uid: String, eventStreamId: String, file: WMUploadFile, additionalContext: Map<String, Any> = emptyMap())
    fun uploadJobDescriptionUrl(uid: String, eventStreamId: String, url: String, additionalContext: Map<String, Any> = emptyMap())
    fun reevaluateMix(uid: String, eventStreamId: String, mixId: String, additionalContext: Map<String, Any> = emptyMap())
    /** Runs a failed upload's pipeline again on its file; the mirrored upload shows the progress. */
    fun retryUpload(uid: String, uploadId: String, additionalContext: Map<String, Any> = emptyMap())
    /** Deletes a failed upload and its files; the mirror of the user's uploads drops it. */
    fun deleteUpload(uid: String, uploadId: String, additionalContext: Map<String, Any> = emptyMap())
    fun fetchFileUrls(uid: String, mixIds: Set<String>, additionalContext: Map<String, Any> = emptyMap())
}

fun WMJobDescriptionSyncDataSource(api: WagemixClientAPI, localContext: WMLocalContextDataSource): WMJobDescriptionSyncDataSource {
    return WMJobDescriptionSyncDataSourceImpl(api = api, localContext = localContext)
}

internal class WMJobDescriptionSyncDataSourceImpl(
    private val api: WagemixClientAPI,
    localContext: WMLocalContextDataSource,
    scope: CoroutineScope = CoroutineScope(SupervisorJob())
): WMBaseSyncDataSource<WMJobDescriptionSyncEvent<*>>(localContext, scope), WMJobDescriptionSyncDataSource {

    override fun uploadJobDescription(uid: String, eventStreamId: String, file: WMUploadFile, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            pipeline(
                operation = OP_UPLOAD_FILE,
                uid = uid,
                eventStreamId = eventStreamId,
                sequenceId = sequenceId,
                initial = { WMJobDescriptionSyncEvent.UploadFile.Initial(uid, eventStreamId, file.name, sequenceId, additionalContext) },
                success = { WMJobDescriptionSyncEvent.UploadFile.Success(uid, eventStreamId, file.name, sequenceId, additionalContext) },
                failure = { error ->
                    WMJobDescriptionSyncEvent.UploadFile.Failure(uid, eventStreamId, file.name, error, sequenceId, additionalContext)
                },
                start = {
                    quotaAware {
                        api.uploadFile {
                            eventStreamId(eventStreamId)
                            body(FileUploadRequestBody(fileName = file.name, contentType = file.contentType, fileContent = file.bytes))
                        }.throwUnlessSuccess { it?.message ?: "The upload was refused" }
                    }
                }
            )
        }
    }

    override fun uploadJobDescriptionUrl(uid: String, eventStreamId: String, url: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            pipeline(
                operation = OP_UPLOAD_URL,
                uid = uid,
                eventStreamId = eventStreamId,
                sequenceId = sequenceId,
                initial = { WMJobDescriptionSyncEvent.UploadUrl.Initial(uid, eventStreamId, url, sequenceId, additionalContext) },
                success = { WMJobDescriptionSyncEvent.UploadUrl.Success(uid, eventStreamId, url, sequenceId, additionalContext) },
                failure = { error -> WMJobDescriptionSyncEvent.UploadUrl.Failure(uid, eventStreamId, url, error, sequenceId, additionalContext) },
                start = {
                    quotaAware {
                        api.uploadJDUrl {
                            eventStreamId(eventStreamId)
                            body(WMJDUrlUpload.kxs(url = url))
                        }.throwUnlessSuccess { it?.message ?: "The posting URL was refused" }
                    }
                }
            )
        }
    }

    override fun reevaluateMix(uid: String, eventStreamId: String, mixId: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            pipeline(
                operation = OP_REEVALUATE,
                uid = uid,
                eventStreamId = eventStreamId,
                sequenceId = sequenceId,
                initial = { WMJobDescriptionSyncEvent.Reevaluate.Initial(uid, eventStreamId, mixId, sequenceId, additionalContext) },
                success = { WMJobDescriptionSyncEvent.Reevaluate.Success(uid, eventStreamId, mixId, sequenceId, additionalContext) },
                failure = { error ->
                    WMJobDescriptionSyncEvent.Reevaluate.Failure(uid, eventStreamId, mixId, error, sequenceId, additionalContext)
                },
                start = {
                    api.reevaluateJD {
                        eventStreamId(eventStreamId)
                        body(WMReevaluateJD.kxs(storedMixId = mixId))
                    }.throwUnlessSuccess { it?.message ?: "The re-evaluation was refused" }
                }
            )
        }
    }

    override fun retryUpload(uid: String, uploadId: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_RETRY,
                scope = uploadId,
                sequenceId = sequenceId,
                initial = { WMJobDescriptionSyncEvent.RetryUpload.Initial(uid, uploadId, sequenceId, additionalContext) },
                success = { WMJobDescriptionSyncEvent.RetryUpload.Success(uid, uploadId, sequenceId, additionalContext) },
                failure = { error -> WMJobDescriptionSyncEvent.RetryUpload.Failure(uid, uploadId, error, sequenceId, additionalContext) },
                remote = {
                    // A retry is held to the plan's uploads like a new upload (a 402 is the limit).
                    quotaAware {
                        api.retryJobDescriptionUpload { uploadId(uploadId) }
                            .throwUnlessSuccess { it?.message ?: "The upload could not be retried" }
                    }
                },
                // The mirror of the user's uploads carries the restarted pipeline's progress.
                local = { }
            )
        }
    }

    override fun deleteUpload(uid: String, uploadId: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_DELETE,
                scope = uploadId,
                sequenceId = sequenceId,
                initial = {
                    WMJobDescriptionSyncEvent.DeleteUpload.Initial(uid, uploadId, sequenceId, additionalContext)
                },
                success = {
                    WMJobDescriptionSyncEvent.DeleteUpload.Success(uid, uploadId, sequenceId, additionalContext)
                },
                failure = { error ->
                    WMJobDescriptionSyncEvent.DeleteUpload.Failure(uid, uploadId, error, sequenceId, additionalContext)
                },
                remote = {
                    api.deleteJobDescriptionUpload { uploadId(uploadId) }
                        .throwUnlessSuccess { it?.message ?: "The upload could not be deleted" }
                },
                // The mirror of the user's uploads drops it.
                local = { }
            )
        }
    }

    override fun fetchFileUrls(uid: String, mixIds: Set<String>, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_FILE_URLS,
                scope = mixIds.sorted().joinToString(","),
                sequenceId = sequenceId,
                initial = { WMJobDescriptionSyncEvent.FetchFileUrls.Initial(uid, mixIds, sequenceId, additionalContext) },
                success = { WMJobDescriptionSyncEvent.FetchFileUrls.Success(uid, mixIds, sequenceId, additionalContext) },
                failure = { error -> WMJobDescriptionSyncEvent.FetchFileUrls.Failure(uid, mixIds, error, sequenceId, additionalContext) },
                remote = {
                    api.fetchStoredMixJDFileUrls { mixIds.forEach { addMixId(it) } }
                        .dataOrThrow { it?.message ?: "No files in the response" }
                },
                // A mix the response leaves out has no file any more, so an older entry for it goes too.
                local = { files ->
                    val kept = localContext.retrieveUser(uid).jobDescriptionFiles() - mixIds
                    localContext.upsertJobDescriptionFiles(uid, kept + files)
                }
            )
        }
    }

    /**
     * Starts the pipeline with [start], then follows the progress stream into the user's local
     * context. A final unsuccessful update is the remote error; nothing else is written locally.
     */
    private suspend fun pipeline(
        operation: String,
        uid: String,
        eventStreamId: String,
        sequenceId: Uuid,
        initial: () -> WMJobDescriptionSyncEvent<*>,
        success: () -> WMJobDescriptionSyncEvent<*>,
        failure: (Throwable) -> WMJobDescriptionSyncEvent<*>,
        start: suspend () -> Unit
    ) {
        guarded(
            operation = operation,
            scope = eventStreamId,
            sequenceId = sequenceId,
            initial = initial,
            success = success,
            failure = failure,
            remote = {
                localContext.upsertUploadProgress(uid, eventStreamId, WMUploadProgress.of("", inProgress = true, successful = true, mixId = null))
                start()
                flow { api.createFileUploadUpdateListener(eventStreamId) { emit(it) } }
                    .onEach { update ->
                        val mixId = if (!update.inProgress && update.successful) update.message else null
                        val progress = WMUploadProgress.of(update.message, update.inProgress, update.successful, mixId)
                        localContext.upsertUploadProgress(uid, eventStreamId, progress)
                        if (!update.inProgress && !update.successful) {
                            val reason = update.reason
                                ?: throw WMRemoteException(status = 500, message = update.message)
                            throw WMUploadNotAcceptedException(reason, update.message)
                        }
                    }
                    .collect()
            },
            local = { }
        )
    }

    /**
     * A 402 from the API is the Basic plan's quota, and a 422 a file its virus scan rejected before storing it; the UI
     * names both. Everything else passes through.
     */
    private suspend fun quotaAware(block: suspend () -> Unit) {
        try {
            block()
        } catch (e: WMRemoteException) {
            when {
                e.isPaymentRequired -> throw WMQuotaExceededException(e.message)
                e.status == 422 -> {
                    throw WMUploadNotAcceptedException(WMUploadFailureReason.Malicious, e.message.orEmpty())
                }
                else -> throw e
            }
        }
    }

    private companion object {
        const val OP_UPLOAD_FILE = "jobDescription.uploadFile"
        const val OP_UPLOAD_URL = "jobDescription.uploadUrl"
        const val OP_REEVALUATE = "jobDescription.reevaluate"
        const val OP_RETRY = "jobDescription.retry"
        const val OP_DELETE = "jobDescription.delete"
        const val OP_FILE_URLS = "jobDescription.fileUrls"
    }
}
